import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API } from '../config';
import { adapterToken } from './serviziAdapter';
import AddressCascade from '../components/AddressCascade';
import VodafoneBadge from '../components/VodafoneBadge';
import { useVicine } from '../components/VicineAgente';
import ZonaMap from '../components/ZonaMap';
import { APP_VERSION } from '../version';
import TourGuidato from '../components/TourGuidato';
import { makeCoperturaPdf as makeCoperturaPdfBase, scaricaFile } from '../utils/coperturaPdf';
import LucchettoConsistenze from '../components/LucchettoConsistenze';
import {
  ESITO_LABEL, PRIORITA_TECH, colonneDaEsito, colonneOk, miglioreTecnologia, covStato,
  fmtVelTim, esitoColorTim, esitoText, esitoStyle,
} from './coperturaEsito';
import './publicDesktop.css';

// Dashboard DESKTOP del link pubblico (rotta /pd/<token>).
// Versione parallela e isolata: NON tocca /p/<token> (in uso dai collaboratori).
// Riusa lo stesso backend via adapterToken -> davvero funzionante, non un mockup.
// Strumenti reali del link pubblico: Numero (456 + Vodafone auto) e Copertura, capability-gated.
// Uno strumento NON abilitato non viene mostrato affatto. Nessuna emoji: icone SVG stile PWA agente.

function tokenFromUrl() {
  const m = window.location.pathname.match(/^\/pd?\/([^/]+)/);   // /p/<token> (e /pd/ per compatibilità)
  return m ? decodeURIComponent(m[1]) : '';
}

// ── Icone (stile SF Symbols, tratto 2.1 arrotondato) ──
const IcoNum = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.1a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />
  </svg>
);
const IcoCov = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2" />
    <path d="M16.24 7.76a6 6 0 0 1 0 8.49M7.76 16.24a6 6 0 0 1 0-8.49" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 19.07a10 10 0 0 1 0-14.14" />
  </svg>
);
const IcoDom = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9.3" /><path d="M2.7 12h18.6" />
    <path d="M12 2.7c2.55 2.5 3.98 5.85 3.98 9.3S14.55 18.8 12 21.3C9.45 18.8 8.02 15.45 8.02 12S9.45 5.2 12 2.7z" />
  </svg>
);
const IcoVic = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" />
  </svg>
);
const IcoZona = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="8" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /><circle cx="12" cy="12" r="2.5" />
  </svg>
);
const IcoLuc = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="10.5" width="16" height="10.5" rx="2.2" />
    <path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" /><circle cx="12" cy="15.6" r="1.4" />
  </svg>
);
const IcoInd = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10.5" cy="10.5" r="6.7" /><path d="m20.5 20.5-4.7-4.7" />
  </svg>
);
// Rileva il browser per personalizzare la guida d'installazione (l'ordine conta: Edge/Opera prima di Chrome).
function rilevaBrowser() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\//.test(ua) || /Opera/.test(ua)) return 'opera';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'other';
}
const Sun = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" /></svg>);
const Moon = () => (<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>);
// glifo che ricorda l'icona "Installa app" della barra indirizzi di Edge/Chrome (schermo + freccia giù)
const InstallGlyph = () => (<svg className="inst-ic" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12.5" rx="2" /><path d="M8.5 20.5h7M12 16.5v4" /><path d="M12 7.5v4.2m0 0 2-2m-2 2-2-2" /></svg>);

// Player audio moderno per la registrazione del 456 (play/pausa, seek, tempo, download+elimina in barra).
const _fmtT = (s) => { if (!s || !isFinite(s)) return '0:00'; const m = Math.floor(s / 60), x = Math.floor(s % 60); return `${m}:${x < 10 ? '0' : ''}${x}`; };
function AudioPlayer({ src, onDelete }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) a.play(); else a.pause(); };
  const seek = (e) => { const a = ref.current; if (!a || !dur) return; const r = e.currentTarget.getBoundingClientRect(); a.currentTime = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)) * dur; };
  return (
    <div className="aud">
      <audio ref={ref} preload="metadata" src={src}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCur(e.target.currentTime)} onLoadedMetadata={(e) => setDur(e.target.duration)} />
      <button className="aud-pp" onClick={toggle} aria-label={playing ? 'Pausa' : 'Riproduci'}>
        {playing
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1.3" /><rect x="14" y="5" width="4" height="14" rx="1.3" /></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.2v13.6a1 1 0 0 0 1.5.87l11-6.8a1 1 0 0 0 0-1.74l-11-6.8A1 1 0 0 0 8 5.2z" /></svg>}
      </button>
      <div className="aud-mid">
        <div className="aud-bar" onClick={seek}><i style={{ width: `${dur ? (cur / dur) * 100 : 0}%` }} /></div>
        <div className="aud-t">{_fmtT(cur)} / {_fmtT(dur)}</div>
      </div>
      <a className="aud-ic" href={src} download title="Scarica registrazione">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
      </a>
      {onDelete && <button className="aud-ic" onClick={onDelete} title="Elimina registrazione">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
      </button>}
    </div>
  );
}

// Stato di un passo del Dominio (n456/copertura/lucchetto) per la UI.
function domStepText(s) {
  if (!s) return '—';
  if (s.stato === 'in_corso') return 'in corso…';
  if (s.stato === 'attesa') return 'in attesa…';
  if (s.stato === 'saltato') return `saltato${s.motivo ? ' · ' + s.motivo : ''}`;
  if (s.stato === 'errore') return `errore${s.motivo ? ' · ' + s.motivo : ''}`;
  return s.esito || 'fatto';
}
function domStepDone(s) { return s && (s.stato === 'fatto' || (s.esito && s.stato !== 'in_corso' && s.stato !== 'attesa')); }
const DOM_STEPS = [{ k: 'n456', l: '456', c: 'var(--c456)' }, { k: 'copertura', l: 'Copertura', c: 'var(--ccov)' }, { k: 'lucchetto', l: 'Lucchetto', c: 'var(--gold)' }];

// ── Matrice copertura: STESSO layout della PWA agente (RIEPILOGO a gruppi + tab per tecnologia),
//    ma vestita coi token della pagina link (.pubdk, accento = colore copertura). ──
const COV_COLS = [
  { k: 'fwa_5g', l: 'FWA 5G', t: 'ok' }, { k: 'fwa_4g', l: 'FWA 4G', t: 'ok' },
  { k: 'fibra_10g', l: 'Fibra 10G', t: 'ok' }, { k: 'fibra_nere', l: 'Fibra nere', t: 'ok' },
  { k: 'fibra_bianche', l: 'Fibra bianche', t: 'ok' }, { k: 'xdsl_30m', l: 'xDSL 30M', t: 'speed' },
  { k: 'xdsl_fast200', l: 'xDSL Fast 200', t: 'speed' }, { k: 'adsl_prof', l: 'ADSL Prof.', t: 'ok' },
  { k: 'ftto', l: 'FTTO', t: 'fascia' }, { k: 'eth_silver', l: 'Eth. Silver', t: 'fascia' },
  { k: 'eth_gold', l: 'Eth. Gold', t: 'fascia' }, { k: 'fibra_prof_10g', l: 'Fibra Prof. 10G', t: 'ok' },
  { k: 'fibra_prof', l: 'Fibra Prof.', t: 'ok' },
];
const COV_TABS = [
  { id: 'RIEPILOGO' },
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof', 'ftto', 'eth_silver', 'eth_gold'] },
];
const COV_RIEP = [
  { id: 'FWA', keys: ['fwa_5g', 'fwa_4g'] },
  { id: 'PACCHETTI', keys: ['fibra_10g', 'fibra_nere', 'fibra_bianche'] },
  { id: 'PROFILI', keys: ['fibra_prof_10g', 'fibra_prof', 'xdsl_30m', 'xdsl_fast200', 'adsl_prof'] },
  { id: 'DEDICATA', keys: ['ftto', 'eth_silver', 'eth_gold'] },
];
const COV_LABEL = Object.fromEntries(COV_COLS.map((c) => [c.k, c.l]));
const CheckIco = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>);
function covCell(c, v) {
  v = v || {};
  if (c.t === 'ok') return v.esito === 'OK' ? <CheckIco /> : (v.esito === 'GIALLO' ? <span style={{ color: 'var(--warn)' }}>~</span> : <span style={{ color: 'var(--faint)' }}>—</span>);
  if (c.t === 'speed') return (v.esito === 'OK' && (v.up || v.down)) ? <span style={{ color: 'var(--ink)' }}>{v.up || '?'}/{v.down || '?'}</span> : <span style={{ color: 'var(--faint)' }}>—</span>;
  return <span style={{ color: (v.fascia || v.esito === 'OK') ? 'var(--ink)' : 'var(--faint)' }}>{v.fascia || (v.esito === 'OK' ? 'OK' : '—')}</span>;
}
// PDF del risultato copertura: generatore condiviso con la PWA agente (src/utils/coperturaPdf.js).
function _covPdfVal(k, v) {
  v = v || {};
  const c = COV_COLS.find((x) => x.k === k) || {};
  if (c.t === 'speed') return (v.up || v.down) ? `${v.up || '?'}/${v.down || '?'} Mbps` : 'Disponibile';
  if (c.t === 'fascia') return v.fascia ? String(v.fascia) : 'Disponibile';
  return 'Disponibile';
}
const _covPdfOpts = (d) => ({
  colonne: (d.colonne && Object.keys(d.colonne).length) ? d.colonne : colonneDaEsito(d.esito),
  gruppi: COV_RIEP, label: COV_LABEL, valore: _covPdfVal,
});
const makeCoperturaPdf = (d, utente) => makeCoperturaPdfBase(d, utente, _covPdfOpts(d));

function CovMatrice({ d, utente }) {
  const [tab, setTab] = useState('RIEPILOGO');
  const [shareOpen, setShareOpen] = useState(false);
  const pdfRef = useRef(null);
  const openShare = () => {
    setShareOpen((o) => !o);
    if (!pdfRef.current) makeCoperturaPdf(d, utente).then((f) => { pdfRef.current = f; }).catch(() => {});
  };
  const getPdf = async () => pdfRef.current || (pdfRef.current = await makeCoperturaPdf(d, utente));
  const scarica = scaricaFile;
  const doDownload = async () => { try { scarica(await getPdf()); } catch (e) { /* noop */ } setShareOpen(false); };
  const doEmail = async () => {
    try {
      const file = pdfRef.current;
      // Web Share con file (Windows/Edge/Chrome, iOS/Android): apre il pannello di condivisione da cui
      // scegliere Posta -> la mail parte GIA' con il PDF allegato.
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Analisi Copertura', text: "In allegato l'analisi di copertura di rete." });
      } else {
        // Fallback: scarica il PDF e apre il client di posta (mailto non può allegare da web) con il testo pronto.
        scarica(await getPdf());
        window.location.href = 'mailto:?subject=' + encodeURIComponent('Analisi Copertura')
          + '&body=' + encodeURIComponent("In allegato trovi l'analisi di copertura (PDF appena scaricato: allegalo a questa email).");
      }
    } catch (e) { /* annullato dall'utente */ }
    setShareOpen(false);
  };
  if (!d || covStato(d) !== 'ok') return null;
  const colonne = (d.colonne && Object.keys(d.colonne).length) ? d.colonne : colonneDaEsito(d.esito);
  const t = COV_TABS.find((x) => x.id === tab) || COV_TABS[0];
  const isRiep = tab === 'RIEPILOGO';
  const keys = t.keys || [];
  const groups = isRiep
    ? COV_RIEP.map((g) => ({ id: g.id, keys: g.keys.filter((k) => (colonne[k] || {}).esito === 'OK') })).filter((g) => g.keys.length)
    : [{ id: t.id, keys }];
  const totale = groups.reduce((n, g) => n + g.keys.length, 0);
  return (
    <div className="covmx">
      <div className="covmx-top">
        <div className="covmx-tabs">
          {COV_TABS.map((x) => (
            <button key={x.id} type="button" className={`covmx-tab${tab === x.id ? ' on' : ''}`} onClick={() => setTab(x.id)}>{x.id}</button>
          ))}
        </div>
        <div className="covmx-share-wrap">
          <button type="button" className={`covmx-share${shareOpen ? ' on' : ''}`} onClick={openShare} title="Condividi il risultato">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></svg>
            Condividi
          </button>
          {shareOpen && (
            <>
              <div className="covmx-pop-ov" onClick={() => setShareOpen(false)} />
              <div className="covmx-pop">
                <button type="button" className="cpop cpop-dl" title="Scarica il PDF" onClick={doDownload}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
                </button>
                <button type="button" className="cpop cpop-mail" title="Invia per email (con allegato)" onClick={doEmail}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 6.5 8.5 6 8.5-6" /></svg>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <div className="covmx-body">
        {totale === 0 ? (
          <div className="covmx-empty">{isRiep ? 'Nessuna tecnologia vendibile su questo indirizzo.' : 'Nessun dato in questa sezione.'}</div>
        ) : (
          <div className={isRiep ? 'covmx-grid' : ''}>
            {groups.map((g) => (
              <div key={g.id}>
                {isRiep && <div className="covmx-gh">{g.id}</div>}
                {g.keys.map((k) => { const c = COV_COLS.find((x) => x.k === k) || { l: COV_LABEL[k] }; return (
                  <div key={k} className="covmx-row"><span className="covmx-l">{c.l}</span><span>{covCell(c, colonne[k])}</span></div>
                ); })}
              </div>
            ))}
          </div>
        )}
        {!d.colonne && !isRiep && <div className="covmx-note">Riepilogo da verifica precedente: dettaglio velocità/fascia non disponibile.</div>}
      </div>
    </div>
  );
}
// indirizzo copertura: puo' essere una STRINGA o un OGGETTO {comune,via,civico} -> sempre stringa (mai oggetto in JSX)
// Dal sito completo al dominio leggibile: nella riga serve il nome, non l'indirizzo intero.
const dominioBreve = (url) => (url || '').replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');

const covIndirizzo = (a) => {
  if (!a) return '';
  if (typeof a === 'string') return a;
  return `${a.via || ''} ${a.civico || ''}${a.comune ? ' · ' + a.comune : ''}`.replace(/\s+/g, ' ').trim();
};
const fmtData = (at) => at ? new Date(at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';
const NUM_PHASES = { avvio: 'Avvio…', registrazione: 'Chiamata al 456…', trascrizione: 'Analisi risposta…', attesa: 'Attesa…', analisi: 'Analisi esito…', standby: 'In standby…', vodafone: 'Verifica Vodafone…' };

// ── Contatori con grafico (variante C): sparkline mensile + ciambella composizione ──
let _sparkId = 0;
function Spark({ data, h = 30, color, fill = true }) {
  const arr = (data && data.length > 1) ? data : [0, ...(data || [0])];
  const min = Math.min(...arr), max = Math.max(...arr), rng = (max - min) || 1, W = 100, pad = 3;
  const step = (W - 2 * pad) / (arr.length - 1);
  const pts = arr.map((v, i) => [pad + i * step, h - pad - ((v - min) / rng) * (h - 2 * pad)]);
  const line = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = line + ` L${(W - pad).toFixed(1)} ${h - pad} L${pad} ${h - pad} Z`;
  const gid = 'spk' + (++_sparkId);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${W} ${h}`} preserveAspectRatio="none" style={{ display: 'block', overflow: 'visible' }}>
      {fill && <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".32" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>}
      {fill && <path d={area} fill={`url(#${gid})`} />}
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
function Donut({ pct, c1, c2, size = 78 }) {
  const r = size / 2 - 6, cx = size / 2, cy = size / 2, C = 2 * Math.PI * r, off = C * (1 - Math.max(0, Math.min(1, pct)));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={c2} strokeWidth="8" />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={c1} strokeWidth="8" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off} transform={`rotate(-90 ${cx} ${cy})`} />
      <text x={cx} y={cy + 4.5} textAnchor="middle" fontSize="15" fontWeight="800" fill="var(--ink)" fontFamily="inherit">{Math.round((pct || 0) * 100)}%</text>
    </svg>
  );
}
const STRU_C = [
  { key: 'num', label: 'Numero', color: '#e59500' },
  { key: 'cov', label: 'Copertura', color: '#3283bd' },
  { key: 'dom', label: 'Dominio', color: '#e0662e' },
  { key: 'ind', label: 'Indaga', color: '#e59500' },
];
const STRU_ICO = { num: IcoNum, cov: IcoCov, dom: IcoDom, ind: IcoInd };

export default function PublicDesktopPage({ adapter: adapterProp }) {
  // `adapter` arriva da fuori quando la console la usa l'AGENTE (AgentePage): stessa
  // pagina, altra provenienza dei dati. Senza, e' il link collaboratore e l'adattatore
  // si costruisce dal token nell'indirizzo — come ha sempre fatto.
  const token = tokenFromUrl();
  const adapter = React.useMemo(() => adapterProp || adapterToken(token), [adapterProp, token]);
  const eAgente = adapter.variant === 'agente';
  const [caps, setCaps] = useState(null);        // { nome, vodafone, copertura, usi_oggi, tetto } | 'invalid' | 'error'
  const [light, setLight] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);   // nuova versione disponibile sul server
  const [notifPerm, setNotifPerm] = useState(() => (typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'));
  const [tourOpen, setTourOpen] = useState(false);       // tutorial guidato (spotlight)
  const [stats, setStats] = useState(null);
  const [statsMese, setStatsMese] = useState(null);   // dati mensili per i contatori con grafico
  const [crono, setCrono] = useState([]);
  const [tool, setTool] = useState('num');
  const [numero, setNumero] = useState('');
  const [cov, setCov] = useState({ provincia: '', comune: '', indirizzo: '', civico: '' });
  const [dominioVal, setDominioVal] = useState('');
  const [indagaNum, setIndagaNum] = useState('');
  const [cf, setCf] = useState('');                  // codice fiscale per il Lucchetto
  const [lucAperto, setLucAperto] = useState(null);  // dialog consistenze
  const [ssmOpen, setSsmOpen] = useState(false);     // dialog password SalesMate (solo agente)
  const [ssmPwd, setSsmPwd] = useState('');
  const [ssmErr, setSsmErr] = useState('');
  const [ssmBusy, setSsmBusy] = useState(false);
  const [notifiche, setNotifiche] = useState([]);    // notifiche agente non lette
  const [notifOpen, setNotifOpen] = useState(false);
  const [azSotto, setAzSotto] = useState(null);      // azienda sotto il mouse NELL'ELENCO
  const [azMappa, setAzMappa] = useState(null);      // azienda toccata SULLA MAPPA
  const [jobs, setJobs] = useState({});          // { num, cov, dom, ind } job per strumento
  const [toasts, setToasts] = useState([]);
  const [detail, setDetail] = useState(null);    // riga di cronologia aperta in dettaglio (dominio/copertura)
  const [notif, setNotif] = useState(() => new Set());   // strumenti con risultato PRONTO non ancora visto (pallino rosso)
  const toolRef = useRef(tool);                  // strumento attivo "fresco" leggibile dentro i poll
  toolRef.current = tool;
  // segna un risultato PRONTO: accende il pallino rosso solo se NON stai già guardando quello strumento
  const segnalaPronto = (key) => setNotif(s => { const n = new Set(s); n.add(key); return n; });
  const [installEvt, setInstallEvt] = useState(null);   // evento beforeinstallprompt (installa su Windows)
  const [showInstall, setShowInstall] = useState(false); // banner "installa l'app" (a ogni apertura se NON installata)
  const [browser] = useState(rilevaBrowser);            // per la guida su misura del browser
  const polls = useRef({});
  const covMeta = useRef({});
  const startPollRef = useRef(null);
  // Chi sta usando la console: serve a «Vicine» per ritrovare l'ultima ricerca dopo un
  // ricaricamento (anche forzato). Per l'agente e' il suo utente, per il link il token.
  const vicKey = (caps && caps !== 'invalid' && caps !== 'error' && (caps.username || caps.nome)) || token || null;
  const vicMap = useVicine(adapter, vicKey); // aziende attorno a un punto scelto sulla mappa
  const [punto, setPunto] = useState(null);   // punto di riferimento cliccato sulla mappa {lat, lon}
  const [sel, setSel] = useState(() => new Set());   // aziende Vicine selezionate per l'import
  const [imp, setImp] = useState(null);              // stato job import Vicine
  const impWas = useRef(false);

  const caricaStats = useCallback(() => {
    adapter.loadStats().then(setStats).catch(() => {});
    adapter.loadCronologia().then(setCrono).catch(() => {});
    if (adapter.loadStatsMensile) adapter.loadStatsMensile().then(setStatsMese).catch(() => {});
  }, [adapter]);

  const caricaCaps = useCallback(() => {
    if (!token && !adapterProp) { setCaps('invalid'); return; }   // link senza token
    setCaps(null);
    adapter.loadCaps()
      .then(r => { setCaps(r); caricaStats(); })
      .catch((e) => setCaps(e.response?.status === 403 ? 'invalid' : 'error'));
  }, [adapter, caricaStats, token, adapterProp]);
  useEffect(() => { caricaCaps(); }, [caricaCaps]);
  // tutorial guidato alla PRIMA apertura del link (una sola volta; poi rivedibile dall'icona "?")
  useEffect(() => {
    if (!caps || caps === 'invalid' || caps === 'error') return;
    try { if (!localStorage.getItem('pdTutorialVisto')) setTourOpen(true); } catch (e) { /* no-op */ }
  }, [caps]);
  const chiudiTour = () => { setTourOpen(false); try { localStorage.setItem('pdTutorialVisto', '1'); } catch (e) { /* no-op */ } };

  // Notifiche dell'agente: le verifiche finiscono anche a pagina chiusa, quindi l'esito
  // va ritrovato al rientro. Solo per l'agente: il link collaboratore non ne ha.
  const caricaNotifiche = useCallback(() => {
    if (!adapter.loadNotifiche) return;
    adapter.loadNotifiche().then(r => setNotifiche(r?.items || [])).catch(() => {});
  }, [adapter]);
  useEffect(() => {
    if (!adapter.loadNotifiche) return undefined;
    caricaNotifiche();
    const id = setInterval(caricaNotifiche, 30000);
    return () => clearInterval(id);
  }, [adapter, caricaNotifiche]);

  // refresh leggero di stats/cronologia
  useEffect(() => {
    if (!caps || caps === 'invalid' || caps === 'error') return undefined;
    const id = setInterval(caricaStats, 20000);
    return () => clearInterval(id);
  }, [caps, caricaStats]);

  useEffect(() => () => { Object.values(polls.current).forEach(clearInterval); }, []);

  // se lo strumento attivo non è abilitato, torna a Numero
  useEffect(() => {
    if (!caps || caps === 'invalid' || caps === 'error') return;
    if ((tool === 'luc' && !(caps.lucchetto && adapter.lucchettoStart)) || (tool === 'cov' && !caps.copertura) || (tool === 'dom' && !caps.dominio) || (tool === 'zona' && !caps.vicine) || (tool === 'ind' && !caps.indaga)) setTool('num');
  }, [tool, caps]);
  // il pallino "pronto" di uno strumento si spegne appena quello strumento e' attivo (l'hai visto):
  // vale anche se il job finisce mentre lo stai già guardando.
  useEffect(() => {
    setNotif(s => { if (!s.has(tool)) return s; const n = new Set(s); n.delete(tool); return n; });
  }, [tool, jobs]);
  // Vicine import: azzera la selezione a ogni nuova ricerca; poll del job; avviso a fine import.
  useEffect(() => { setSel(new Set()); }, [vicMap.items]);
  // Ultima ricerca ripristinata dopo un ricaricamento: rimetti la mappa dov'era, altrimenti
  // tornano le aziende ma la vista riparte dall'Italia intera.
  useEffect(() => {
    if (!punto && vicMap.pos && vicMap.pos.lat != null) setPunto({ lat: vicMap.pos.lat, lon: vicMap.pos.lon });
  }, [vicMap.pos]);   // eslint-disable-line
  // Scelta un segno sulla mappa, la sua riga si porta in vista nell'elenco a destra:
  // altrimenti nell'elenco lungo l'azienda evidenziata resta fuori schermo.
  useEffect(() => {
    if (!azMappa || !azMappa.id) return;
    const el = document.querySelector(`[data-az="${azMappa.id}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [azMappa]);
  useEffect(() => {
    if (!imp?.attivo) return undefined;
    if (!adapter.vicineImportaStato) return undefined;
    const id = setInterval(() => { adapter.vicineImportaStato().then(setImp).catch(() => {}); }, 2000);
    return () => clearInterval(id);
  }, [imp?.attivo, adapter]);
  useEffect(() => {
    if (imp?.attivo) { impWas.current = true; return; }
    if (impWas.current && imp && !imp.attivo) {
      impWas.current = false;
      notifica('Import aziende finito', `${imp.creati || 0} in archivio · ${imp.registro || 0} da completare · ${imp.gia || 0} già presenti`);
      setSel(new Set());
    }
  }, [imp]);
  // Installazione come app di Windows: manifest dedicato (start_url=/pd/<token>) + service worker + prompt install.
  useEffect(() => {
    const el = document.querySelector('link[rel="manifest"]');
    const prev = el ? el.getAttribute('href') : null;
    if (el && token) el.setAttribute('href', `${API}/pubblico/manifest?token=${encodeURIComponent(token)}&pd=1`);
    if ('serviceWorker' in navigator) navigator.serviceWorker.register('/sw.js').catch(() => {});
    const onPrompt = (e) => { e.preventDefault(); setInstallEvt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    // Banner "installa l'app": compare a OGNI apertura, MA solo se NON è già installata (standalone).
    const installata = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || window.navigator.standalone === true;
    if (!installata) setShowInstall(true);
    const onInstalled = () => { setShowInstall(false); setInstallEvt(null); };
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      if (el && prev) el.setAttribute('href', prev);
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [token]);
  // Rileva nuova versione: confronta APP_VERSION (build in esecuzione) con /version.json sul server.
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const r = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' });
        if (!r.ok) return;
        const j = await r.json();
        if (!stop && j && j.version && j.version !== APP_VERSION) setUpdateReady(true);
      } catch (e) { /* offline */ }
    };
    check();
    const id = setInterval(check, 90000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  const alertErr = (e) => {
    const sub = e?.response?.data?.detail || 'Errore, riprova tra qualche secondo';
    const id = 'e' + Date.now();
    setToasts(ts => [...ts, { id, titolo: 'Attenzione', sub }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 6000);
  };
  const notifica = (titolo, sub) => {
    const id = 'n' + Date.now() + Math.round(performance.now());
    setToasts(ts => [...ts, { id, titolo, sub }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 7000);
    // notifica di SISTEMA (Windows/Mac) quando la finestra non e' in primo piano
    try {
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted'
          && (document.hidden || (document.hasFocus && !document.hasFocus()))) {
        const n = new Notification(titolo, { body: sub, icon: '/icon-192.png', tag: 'tim456-' + titolo });
        n.onclick = () => { try { window.focus(); n.close(); } catch (e) { /* noop */ } };
      }
    } catch (e) { /* notifiche non disponibili */ }
  };
  const attivaNotifiche = async () => {
    if (typeof Notification === 'undefined') return;
    try { const p = await Notification.requestPermission(); setNotifPerm(p); } catch (e) { /* noop */ }
  };
  const aggiornaApp = async () => {
    try {
      if (window.caches) { const ks = await caches.keys(); await Promise.all(ks.map(k => caches.delete(k))); }
      if (navigator.serviceWorker) { const rs = await navigator.serviceWorker.getRegistrations(); await Promise.all(rs.map(r => r.update())); }
    } catch (e) { /* noop */ }
    window.location.reload();
  };
  // Check AL VOLO prima di avviare uno strumento: se sul server c'e' una versione diversa,
  // blocca (evita di lavorare con una versione obsoleta, backend magari gia' cambiato).
  const guardVersione = async () => {
    try {
      const r = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store' });
      if (r.ok) {
        const j = await r.json();
        if (j && j.version && j.version !== APP_VERSION) {
          setUpdateReady(true);
          notifica('Aggiorna l\'applicazione', 'Stai usando una versione obsoleta: premi Aggiorna per continuare.');
          return true;
        }
      }
    } catch (e) { /* offline: non bloccare il lavoro */ }
    return false;
  };
  const eliminaReg = () => {
    const id = jobs.num?.id; if (!id) return;
    if (adapter.registrazioneElimina) adapter.registrazioneElimina(id).catch(() => {});
    setJobs(j => ({ ...j, num: { ...j.num, hasReg: false } }));
  };
  const installApp = async () => {
    if (!installEvt) return;
    installEvt.prompt();
    try { await installEvt.userChoice; } catch { /* no-op */ }
    setInstallEvt(null);
  };
  const toggleSel = (id) => setSel(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSelTutte = (list) => setSel(s => (list.length && list.every(it => s.has(it.id))) ? new Set() : new Set(list.map(it => it.id)));
  const importaSel = async () => {
    const chosen = (vicMap.items || []).filter(it => sel.has(it.id)).map(it => ({
      id: it.id, nome: it.nome, indirizzo: it.indirizzo, sito: it.sito, categoria: it.categoria, lat: it.lat, lon: it.lon,
    }));
    if (!chosen.length) return;
    try {
      if (!adapter.vicineImporta) { notifica('Import non disponibile', 'Da qui le aziende non si importano'); return; }
      const r = await adapter.vicineImporta(chosen);
      if (r?.avviato) { setImp({ attivo: true, totale: r.totale, fatti: 0, creati: 0, registro: 0, gia: 0 }); notifica('Import avviato', `${r.totale} aziende — dati recuperati dal sito`); }
      else notifica('Import', r?.motivo || 'Niente da importare');
    } catch (e) { alertErr(e); }
  };

  const startPoll = (tipo, id) => {
    const t0 = Date.now(); let miss = 0;
    // Le due fonti della copertura occupano lo STESSO posto a schermo (uno strumento solo,
    // due modi di rispondere): 'covtim' scrive nello slot 'cov', marcato con fonte.
    const slot = tipo === 'covtim' ? 'cov' : tipo;
    polls.current[id] = setInterval(async () => {
      try {
        const s = tipo === 'num' ? await adapter.ricercaStato(id)
          : tipo === 'covtim' ? await adapter.coperturaTimStato(id)
            : await adapter.coperturaStato(id);
        miss = 0;
        const prog = s.stato === 'in_coda' ? 5 : Math.min(94, Math.round((Date.now() - t0) / (tipo === 'num' ? 900 : 700)));
        setJobs(j => ({ ...j, [slot]: { ...j[slot], stato: s.stato, fase: s.fase, prog, posizione: s.posizione } }));
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          caricaStats();
          segnalaPronto(slot);
          if (tipo === 'covtim') {
            const ris = s.risultato || {};
            const ok = !ris.errore;
            setJobs(j => ({ ...j, cov: { ...j.cov, done: true, prog: 100, ok, risultato: ris, fonte: 'tim', motivo: ris.errore || '' } }));
            notifica('Copertura completata',
              ok ? `${ris.esito || '—'}${ris.tecnologia ? ' · ' + ris.tecnologia : ''}` : (ris.errore || 'non riuscita'));
          } else if (tipo === 'num') {
            const ok = s.risultato && s.risultato !== 'ERRORE';
            setJobs(j => ({ ...j, num: { ...j.num, done: true, prog: 100, ok, risultato: s.risultato, vf: s.gea_risultato || null, inattiva: s.numerazione_inattiva, hasReg: !!s.has_registrazione } }));
            notifica('Verifica numero completata', ok ? `${s.risultato}${s.gea_risultato === 'VODAFONE' ? ' · Vodafone' : ''}` : 'non riuscita');
          } else {
            const ris = s.risultato;
            const ok = ris && ris.trovato;
            // Il motivo sta in s.errore (il job e' fallito PRIMA di produrre un risultato: p.es.
            // login SalesMate rifiutato), tradotto dal server in s.motivo. Cercarlo dentro
            // s.risultato, come si faceva, significava non trovarlo mai e dare la colpa
            // all'indirizzo di chi sta lavorando.
            const motivo = s.motivo || (ris?.errore ? String(ris.errore) : '');
            const credenziali = !!s.servizio_ko;
            const meta = covMeta.current[id]; delete covMeta.current[id];
            // fallita al 1° tentativo -> ritenta UNA volta, ma non se e' un problema di
            // credenziali: il secondo tentativo fallirebbe identico.
            if (!ok && meta && !meta.auto && !credenziali) { avviaCopertura(meta.addr, true); return; }
            setJobs(j => ({ ...j, cov: { ...j.cov, done: true, prog: 100, ok, risultato: ris, fonte: 'sm', motivo, credenziali } }));
            notifica('Copertura completata', ok ? miglioreTecnologia(ris) : (motivo || 'non riuscita'));
          }
        }
      } catch { if (++miss > 6) { clearInterval(polls.current[id]); delete polls.current[id]; setJobs(j => { const n = { ...j }; delete n[slot]; return n; }); } }
    }, 1000);
  };
  startPollRef.current = startPoll;

  const avviaNumero = async (arg) => {
    const n = ((typeof arg === 'string' ? arg : numero) || '').trim(); if (!n) return;
    if (await guardVersione()) return;
    if (typeof arg === 'string') setNumero(arg);
    try {
      const r = await adapter.ricercaStart(n);
      setJobs(j => ({ ...j, num: { id: r.id, stato: 'in_coda', prog: 4, done: false } }));
      startPollRef.current('num', r.id);
    } catch (e) { alertErr(e); }
  };
  // `da` dice da dove e' partita la verifica: lanciata da una riga di Vicine, l'esito non
  // deve strappare via la pagina — si continua a scorrere l'elenco e il risultato si
  // annuncia sull'icona a sinistra, come per gli altri strumenti.
  const avviaDominio = async (arg, da) => {
    const d = ((typeof arg === 'string' ? arg : dominioVal) || '').trim(); if (!d) return;
    if (await guardVersione()) return;
    if (typeof arg === 'string' && !da) setDominioVal(arg);
    try {
      const r = await adapter.dominioStart(d);
      setJobs(j => ({ ...j, dom: { id: r.id, stato: 'estrazione', done: false, dominio: d, da: da || null } }));
      startPollDom(r.id, da || null);
    } catch (e) { alertErr(e); }
  };
  const startPollDom = (id, da) => {
    let miss = 0;
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.dominioStato(id);
        miss = 0;
        setJobs(j => ({ ...j, dom: { ...j.dom, stato: s.stato, dati: s.dati, step: s.step } }));
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          setJobs(j => ({ ...j, dom: { ...j.dom, done: true, stato: s.stato, dati: s.dati, step: s.step } }));
          caricaStats();
          segnalaPronto('dom');
          if (da) {
            segnalaPronto('zona');                                   // pallino sull'icona Vicine
            if (vicMap.segnaAnalizzata) vicMap.segnaAnalizzata(da);
          }
          notifica('Verifica dominio completata', s.dati?.ragione_sociale || (s.stato === 'errore' ? 'non riuscita' : ''));
        }
      } catch { if (++miss > 6) { clearInterval(polls.current[id]); delete polls.current[id]; setJobs(j => { const n = { ...j }; delete n.dom; return n; }); } }
    }, 1500);
  };
  const avviaIndaga = async (arg) => {
    const n = ((typeof arg === 'string' ? arg : indagaNum) || '').trim(); if (!n) return;
    if (await guardVersione()) return;
    if (typeof arg === 'string') setIndagaNum(arg);
    try {
      const r = await adapter.indagaStart(n);
      setJobs(j => ({ ...j, ind: { id: r.id, stato: 'cerca', done: false } }));
      startPollInd(r.id);
    } catch (e) { alertErr(e); }
  };
  // ── Password SalesMate: e' l'unica password dell'agente, vale per l'app e per gli
  //    strumenti che passano dal portale. Quando TIM la fa cambiare, qui si riallinea. ──
  const salvaSsmate = async () => {
    if (!ssmPwd) { setSsmErr('Inserisci la password SalesMate'); return; }
    setSsmBusy(true); setSsmErr('');
    try {
      await adapter.aggiornaSsmate(ssmPwd);
      setSsmOpen(false); setSsmPwd('');
      adapter.loadCaps().then(setCaps).catch(() => {});
      notifica('Credenziali SalesMate aggiornate', 'Copertura e Lucchetto sbloccati');
    } catch (e) {
      setSsmErr(e.response?.data?.detail || 'Errore, riprova');
    } finally { setSsmBusy(false); }
  };

  // ── Lucchetto: presidio commerciale TIM per codice fiscale (solo agente) ──
  const avviaLucchetto = async () => {
    const c = (cf || '').trim().toUpperCase();
    if (!c || !adapter.lucchettoStart) return;
    if (await guardVersione()) return;
    setCf(c);
    try {
      const r = await adapter.lucchettoStart(c);
      setJobs(j => ({ ...j, luc: { id: r.id, stato: 'in_corso', done: false, cf: c } }));
      startPollLuc(r.id);
    } catch (e) { alertErr(e); }
  };
  const startPollLuc = (id) => {
    let miss = 0;
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.lucchettoStato(id);
        miss = 0;
        setJobs(j => ({ ...j, luc: { ...j.luc, stato: s.stato } }));
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          const ris = s.risultato || {};
          setJobs(j => ({ ...j, luc: { ...j.luc, done: true, stato: s.stato, risultato: ris } }));
          caricaStats();
          segnalaPronto('luc');
          notifica('Lucchetto completato', ris.lucchetto || ris.esito || (s.stato === 'errore' ? 'non riuscito' : '—'));
        }
      } catch { if (++miss > 6) { clearInterval(polls.current[id]); delete polls.current[id]; setJobs(j => { const n = { ...j }; delete n.luc; return n; }); } }
    }, 1500);
  };

  const startPollInd = (id) => {
    let miss = 0;
    polls.current[id] = setInterval(async () => {
      try {
        const s = await adapter.indagaStato(id);
        miss = 0;
        setJobs(j => ({ ...j, ind: { ...j.ind, stato: s.stato, dati: s.dati } }));
        if (s.stato === 'completato' || s.stato === 'errore') {
          clearInterval(polls.current[id]); delete polls.current[id];
          setJobs(j => ({ ...j, ind: { ...j.ind, done: true, stato: s.stato, dati: s.dati } }));
          caricaStats();
          segnalaPronto('ind');
          notifica('Indaga completata', s.dati?.ragione_sociale || (s.stato === 'errore' ? 'non riuscita' : 'nessun risultato'));
        }
      } catch { if (++miss > 6) { clearInterval(polls.current[id]); delete polls.current[id]; setJobs(j => { const n = { ...j }; delete n.ind; return n; }); } }
    }, 1400);
  };
  async function avviaCopertura(addr, auto = false) {
    // Credenziali SalesMate bloccate: il riquadro della password, non l'errore tecnico del
    // server («Credenziali SalesMate da aggiornare dal menu»). Come fa la PWA. La copertura
    // resta comunque disponibile dall'altro pulsante, il sito TIM, che non passa da SalesMate.
    if (!auto && eAgente && caps && caps.ssmate_ko) { setSsmErr(''); setSsmOpen(true); return; }
    const a = (addr && addr.provincia !== undefined) ? addr : cov;
    if (!a.provincia || !a.comune || !a.indirizzo.trim() || !a.civico.trim()) return;
    if (!auto && await guardVersione()) return;
    adapter.coperturaStart(a)
      .then(r => {
        covMeta.current[r.id] = { addr: a, auto: !!auto };
        setJobs(j => ({ ...j, cov: { id: r.id, stato: 'in_coda', prog: 4, done: false, fonte: 'sm' } }));
        startPollRef.current('cov', r.id);
      })
      .catch(alertErr);
  }

  // Seconda fonte: il sito TIM. Non passa da SalesMate, quindi funziona anche quando gli
  // account di sistema hanno la password scaduta.
  async function avviaCoperturaTim(addr) {
    const a = (addr && addr.provincia !== undefined) ? addr : cov;
    if (!a.comune || !a.indirizzo.trim()) return;
    if (await guardVersione()) return;
    adapter.coperturaTimStart(a)   // una firma sola: vedi coperturaTimCorpo
      .then(r => {
        setJobs(j => ({ ...j, cov: { id: r.id, stato: 'in_corso', prog: 4, done: false, fonte: 'tim' } }));
        startPollRef.current('covtim', r.id);
      })
      .catch(alertErr);
  }

  // ── I job girano LATO SERVER: al refresh RIPRISTINO gli id di quelli in corso, poi li mantengo salvati ──
  const LSK = 'pd-jobs:' + token;
  const resumedRef = useRef(false);
  // 1) RIPRISTINO (una volta, al mount): legge gli id salvati PRIMA che la persistenza tocchi lo storage.
  useEffect(() => {
    if (resumedRef.current || !token) return;
    resumedRef.current = true;
    let saved = null;
    try { saved = JSON.parse(localStorage.getItem(LSK) || 'null'); } catch { saved = null; }
    if (!saved) return;
    const add = {};
    ['num', 'cov', 'dom', 'ind'].forEach(k => {
      if (saved[k]?.id) add[k] = { id: saved[k].id, stato: 'in_coda', prog: 8, done: false, resumed: true, fonte: saved[k].fonte };
    });
    if (!Object.keys(add).length) return;
    setJobs(j => ({ ...j, ...add }));
    if (add.num) startPollRef.current && startPollRef.current('num', add.num.id);
    // la copertura ha due fonti: ripesca quella giusta, altrimenti interrogherei l'endpoint sbagliato
    if (add.cov) { covMeta.current[add.cov.id] = { addr: cov, auto: true }; startPollRef.current && startPollRef.current(add.cov.fonte === 'tim' ? 'covtim' : 'cov', add.cov.id); }
    if (add.dom) startPollDom(add.dom.id);
    if (add.ind) startPollInd(add.ind.id);
  }, [token, LSK]); // eslint-disable-line
  // 2) PERSISTENZA: salvo gli id dei job in corso. Salto la PRIMA esecuzione (mount, jobs vuoto)
  //    così non cancello lo storage prima che il ripristino qui sopra l'abbia letto.
  const persistInit = useRef(false);
  useEffect(() => {
    if (!persistInit.current) { persistInit.current = true; return; }
    try {
      const slim = {};
      ['num', 'cov', 'dom', 'ind'].forEach(k => { if (jobs[k]?.id && !jobs[k].done) slim[k] = { id: jobs[k].id, fonte: jobs[k].fonte }; });
      if (Object.keys(slim).length) localStorage.setItem(LSK, JSON.stringify(slim));
      else localStorage.removeItem(LSK);
    } catch { /* storage negato/pieno */ }
  }, [jobs, LSK]);

  const jNum = jobs.num, jCov = jobs.cov, jDom = jobs.dom, jInd = jobs.ind, jLuc = jobs.luc;
  const numRunning = jNum && !jNum.done;
  const covRunning = jCov && !jCov.done;
  const domRunning = jDom && !jDom.done;
  const indRunning = jInd && !jInd.done;
  const anyRunning = numRunning || covRunning || domRunning || indRunning;
  // strumento -> sta lavorando? (per animare l'icona nella colonna a sinistra)
  const lucRunning = jLuc && !jLuc.done;
  const domDaVicine = domRunning && jDom && jDom.da;    // dominio lanciato da una riga di Vicine
  const RUN = { num: numRunning, cov: covRunning, dom: domRunning, ind: indRunning, luc: lucRunning,
                zona: vicMap.busy || !!domDaVicine };

  // ── stati a schermo pieno ──
  const wrap = (inner) => <div className={`pubdk${light ? ' light' : ''}`}><div className="bgfx" />{inner}</div>;
  if (caps === null) return wrap(<div className="full"><div><div className="spin" /><p style={{ marginTop: 14 }}>Carico…</p></div></div>);
  if (caps === 'invalid') return wrap(<div className="full"><div className="card"><h2>Link non valido</h2><p>Questo link pubblico non è valido o è scaduto. Chiedi un nuovo link a chi te l'ha condiviso.</p></div></div>);
  if (caps === 'error') return wrap(<div className="full"><div className="card"><h2>Momentaneamente non disponibile</h2><p>Non riesco a contattare il servizio.</p><button onClick={caricaCaps}>Riprova</button></div></div>);

  // ── strumenti abilitati (i non abilitati NON compaiono) ──
  const tools = [{ key: 'num', label: 'Numero', sub: caps.vodafone ? '456 + Vodafone' : '456', Ico: IcoNum, cls: 't-num' }];
  if (caps.copertura || caps.coperturatim) tools.push({ key: 'cov', label: 'Copertura', sub: 'Fibra / ADSL', Ico: IcoCov, cls: 't-cov' });
  if (caps.lucchetto && adapter.lucchettoStart) tools.push({ key: 'luc', label: 'Lucchetto', sub: 'Presidio commerciale', Ico: IcoLuc, cls: 't-luc' });
  if (caps.dominio) tools.push({ key: 'dom', label: 'Dominio', sub: 'Sito → tutto', Ico: IcoDom, cls: 't-dom' });
  if (caps.indaga) tools.push({ key: 'ind', label: 'Indaga', sub: 'Numero → azienda', Ico: IcoInd, cls: 't-ind' });
  if (caps.vicine) tools.push({ key: 'zona', label: 'Vicine', sub: 'Aziende da mappa', Ico: IcoZona, cls: 't-vic' });

  // ── KPI ──
  // Contatori con grafico (variante C): oggi + attività per strumento + composizione mese
  const cOggi = statsMese?.oggi ?? stats?.oggi ?? 0;
  const cGiorni = statsMese?.giorni || [];
  const cStr = statsMese?.strumenti || {};
  const cTim = statsMese?.tim || 0, cNon = statsMese?.nontim || 0, cTot = cTim + cNon;
  const pctNon = cTot ? cNon / cTot : 0;

  // Lista aziende (riusata da Vicine GPS e Zona mappa)
  const vicLista = (v) => (v.itemsFiltrati && v.itemsFiltrati.length > 0) ? (
    <div className="vlist">
      {v.itemsFiltrati.map((it, i) => {
        const a = it.archivio || {};
        const tag = a.lead ? { c: 'lead', t: 'LEAD' } : a.risultato === 'TIM' ? { c: 'tim', t: 'TIM' } : a.trovato ? { c: 'arc', t: 'archivio' } : null;
        const dist = it.distanza != null ? (it.distanza < 1000 ? `${it.distanza} m` : `${(it.distanza / 1000).toFixed(1)} km`) : '';
        const gia = esitoDominio(it.sito);
        return (
          <div key={it.id || i} className="vrow" onClick={() => v.apri(it)}>
            <div className="vp"><IcoVic size={14} /></div>
            <div className="vtx">
              <div className="vn">{v.analizzate && v.analizzate[it.id] && <span className="vok" title="Già analizzata">✓</span>}{it.nome}</div>
              <div className="vm">
                {it.indirizzo}{dist ? ` · ${dist}` : ''}
                {it.sito && <> · <a className="vsito" href={it.sito} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{dominioBreve(it.sito)}</a></>}
              </div>
            </div>
            {gia && (
              <button className="vocchio" title="Vedi l'esito della verifica dominio"
                onClick={(e) => { e.stopPropagation(); setDetail(gia); }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
              </button>
            )}
            {tag && <span className={`vt ${tag.c}`}>{tag.t}</span>}
            <div className="vact">
              {caps.dominio && it.sito
                ? <button className="btn t-dom" disabled={domRunning}
                    onClick={(e) => { e.stopPropagation(); avviaDominio(it.sito, it.id); }}>
                    {jDom && !jDom.done && jDom.da === it.id ? 'In corso…' : gia ? 'Rifai la verifica' : 'Verifica dominio'}
                  </button>
                : <span className="vnosito">Nessun sito per questa azienda</span>}
              <button className="vmore" style={{ margin: 0, width: 'auto', padding: '6px 12px' }}
                onClick={(e) => { e.stopPropagation(); v.apri(it); }}>Dettaglio e 456</button>
            </div>
          </div>
        );
      })}
      {v.senzaSito > 0 && <div className="vnota">{v.senzaSito === 1 ? '1 società della zona senza sito web non è in elenco' : `${v.senzaSito} società della zona senza sito web non sono in elenco`}</div>}
      {v.next && <button className="vmore" onClick={v.caricaAltre} disabled={v.more}>{v.more ? 'Carico…' : 'Carica altre'}</button>}
    </div>
  ) : null;

  // Verifiche Dominio gia' fatte, per dominio: dalla riga di Vicine si riapre l'esito senza
  // rilanciare niente. La cronologia si ricarica a ogni verifica conclusa, quindi l'occhio
  // compare da solo appena il risultato c'e'.
  // NIENTE useMemo qui: questo punto sta DOPO i ritorni anticipati della pagina (caps che
  // carica, link non valido), quindi al primo render non verrebbe eseguito e al secondo si'
  // — e React si ferma con la pagina bianca. La cronologia e' di poche decine di righe:
  // ricalcolarla a ogni render non costa niente.
  const domGiaFatti = {};
  (crono || []).forEach((d) => { if (d.tipo === 'dominio' && d.dominio) domGiaFatti[dominioBreve(d.dominio)] = d; });
  const esitoDominio = (sito) => (sito ? domGiaFatti[dominioBreve(sito)] : null);

  // Modale dettaglio azienda (riusato da Vicine GPS e Zona mappa)
  const vicModal = (v) => v.aperto ? (
    <div className="pscrim" onClick={() => v.setAperto(null)}>
      <div className="pmodal t-vic" onClick={e => e.stopPropagation()}>
        <h3>{v.aperto.nome}</h3>
        <div className="pl">{v.aperto.indirizzo}</div>
        {v.aperto.loading ? <div className="vmsg">Carico dettaglio…</div>
          : v.aperto.err ? <div className="vmsg">{v.aperto.err}</div>
          : <>
              <div className="pl">{v.aperto.telefono ? `Tel: ${v.aperto.telefono}` : 'Telefono non disponibile'}{v.aperto.sito ? ` · ${v.aperto.sito}` : ''}</div>
              <div className="pa">
                {v.aperto.telefono && <button className="btn t-num" onClick={() => { setTool('num'); avviaNumero(v.aperto.telefono); v.setAperto(null); }}>Verifica 456</button>}
                {caps.dominio && v.aperto.sito && <button className="btn t-dom" onClick={() => { setTool('dom'); avviaDominio(v.aperto.sito); v.setAperto(null); }}>Verifica dominio</button>}
              </div>
            </>}
        <button className="vmore" style={{ marginTop: 12 }} onClick={() => v.setAperto(null)}>Chiudi</button>
      </div>
    </div>
  ) : null;

  const pdTour = [
    { title: 'Benvenuto nel Centro Verifiche', body: 'In pochi passi ti mostro come usarlo. Puoi saltare quando vuoi.' },
    { target: '[data-tour="rail"]', title: 'Gli strumenti', body: 'Qui a sinistra scegli lo strumento: Numero (456), Copertura, Dominio, Indaga… Cliccane uno e compare al centro.', onEnter: () => setTool('num') },
    { target: '[data-tour="tool"]', title: 'Avvia una verifica', body: 'Scrivi il dato (es. il numero) e premi Avvia. L’avanzamento e l’esito compaiono qui e nella cronologia a destra.', onEnter: () => setTool('num') },
    { target: '[data-tour="cron"]', title: 'La cronologia', body: 'Ogni verifica finisce qui, con l’esito. L’icona a occhio apre il dettaglio; dalla Copertura puoi anche condividere un PDF.' },
    // il passo esiste solo se la campanella del permesso e' davvero a schermo: per l'agente
    // la campanella e' una sola (la casella) e il permesso si attiva da dentro il pannello
    ...(notifPerm !== 'unsupported' && !adapter.loadNotifiche ? [{ target: '[data-tour="notif"]', title: 'Notifiche sul computer', body: 'Attiva questa campanella: anche se minimizzi la finestra o lavori su altro, il PC ti avvisa quando un’operazione finisce.' }] : []),
    { target: '[data-tour="ver"]', title: 'Aggiornamenti', body: 'Se in alto compare il pulsante «Aggiorna» che lampeggia, premilo per prendere la versione più recente. Qui sotto vedi sempre la versione installata.' },
    { title: 'Tutto qui!', body: 'Sei pronto. Per rivedere questa guida, premi l’icona «?» in alto a destra.' },
  ];

  return wrap(<>
    {tourOpen && <TourGuidato steps={pdTour} onClose={chiudiTour} />}
    {/* TOP */}
    <div className="top">
      <div className="logo"><span>S</span></div>
      <div className="brand">Centro Verifiche<small>{caps.nome || 'accesso collaboratore'}</small></div>
      <div className="sp" />
      {caps.tetto != null && (
        <div className="chip-q">Oggi <b>{caps.usi_oggi ?? 0}</b>/{caps.tetto}
          <span className="qbar"><i style={{ width: `${Math.min(100, Math.round(((caps.usi_oggi || 0) / Math.max(1, caps.tetto)) * 100))}%` }} /></span>
        </div>
      )}
      {installEvt && (
        <button className="tgl" onClick={installApp} title="Installa app su Windows">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
        </button>
      )}
      {updateReady && (
        <button className="upd-btn" onClick={aggiornaApp} title="Aggiornamento disponibile — premi per aggiornare">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v5h-5" /></svg>
          Aggiorna
        </button>
      )}
      {notifPerm !== 'unsupported' && !adapter.loadNotifiche && (
        <button data-tour="notif" className={`tgl${notifPerm === 'granted' ? ' on' : ''}`} onClick={attivaNotifiche}
          title={notifPerm === 'granted' ? 'Notifiche di sistema attive' : notifPerm === 'denied' ? 'Notifiche bloccate dal browser' : 'Attiva le notifiche sul desktop'}>
          {notifPerm === 'granted'
            ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
            : <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /><line x1="2" y1="2" x2="22" y2="22" /></svg>}
        </button>
      )}
      <button className="tgl" onClick={() => setTourOpen(true)} title="Rivedi il tutorial">
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 2.5-3 4" /><path d="M12 17h.01" /></svg>
      </button>
      {adapter.loadNotifiche && (
        <button className="tgl" onClick={() => setNotifOpen(v => !v)} title="Notifiche"
          style={{ position: 'relative' }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" /></svg>
          {notifiche.length > 0 && <span className="tb-dot" style={{ top: 4, right: 4 }} title={`${notifiche.length} da leggere`} />}
        </button>
      )}
      <button className="tgl" onClick={() => setLight(v => !v)} title="Tema chiaro/scuro">{light ? <Moon /> : <Sun />}</button>
    </div>
    <div className="liveline" />
    {updateReady && (
      <div className="upd-bar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
        <span>È disponibile un <b>aggiornamento importante</b>. Aggiorna per continuare a lavorare in sicurezza.</span>
        <button onClick={aggiornaApp}>Aggiorna ora</button>
      </div>
    )}

    {eAgente && caps.ssmate_ko && (
      <div className="upd-bar" style={{ background: 'rgba(200,30,92,.16)' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="10.5" width="16" height="10.5" rx="2.2" /><path d="M8 10.5V7.2a4 4 0 0 1 8 0v3.3" /></svg>
        <span>La <b>password SalesMate</b> non è più valida: Copertura e Lucchetto sono bloccati.</span>
        <button onClick={() => { setSsmErr(''); setSsmOpen(true); }}>Aggiornala</button>
      </div>
    )}

    <div className="grid">
      {/* LEFT: strumenti */}
      <div className={`rail${updateReady ? ' locked' : ''}`} data-tour="rail">
        <div className="lbl">Strumenti</div>
        {tools.map(t => {
          const busy = RUN[t.key];
          const ready = notif.has(t.key);
          const apri = () => { setNotif(s => { if (!s.has(t.key)) return s; const n = new Set(s); n.delete(t.key); return n; }); setTool(t.key); };
          return (
            <button key={t.key} className={`tool-btn ${t.cls}${tool === t.key ? ' on' : ''}${busy ? ' working' : ''}`} onClick={apri}>
              <span className="ic"><t.Ico size={18} /></span>
              <span>{t.label}<small>{t.sub}</small></span>
              {ready && <span className="tb-dot" title="Risultato pronto" />}
            </button>
          );
        })}
        {/* Agente: la password SalesMate e' l'unica password, e quando TIM la fa cambiare
            Copertura e Lucchetto si bloccano. Da qui si rientra senza uscire dall'app. */}
        {eAgente && adapter.aggiornaSsmate && (
          <button className={`tool-btn t-num${caps.ssmate_ko ? ' working' : ''}`}
            style={{ marginTop: 6 }} onClick={() => { setSsmErr(''); setSsmOpen(true); }}>
            <span className="ic"><IcoLuc size={18} /></span>
            <span>Password<small>{caps.ssmate_ko ? 'da aggiornare' : 'SalesMate'}</small></span>
            {caps.ssmate_ko && <span className="tb-dot" title="Credenziali da aggiornare" />}
          </button>
        )}
        <div className="rail-brand" title="Sonitech">
          <img src="/logo-sonitech.jpg" alt="Sonitech" />
          <span className="rail-ver" data-tour="ver">v{APP_VERSION}</span>
        </div>
      </div>

      {/* CENTER */}
      <div className={`work${updateReady ? ' locked' : ''}`}>
        <div className="kpis kpis-c">
          <div className="kc kc-oggi" style={{ '--kc': '#f0c46a' }}>
            <div className="kc-lab">Oggi</div>
            <div className="kc-val" style={{ color: '#f0c46a' }}>{cOggi}</div>
            <div className="kc-sub">verifiche · su {statsMese?.mese ?? '—'} nel mese</div>
            <div className="kc-chart"><Spark data={cGiorni} h={28} color="#f0c46a" /></div>
          </div>
          <div className="kc kc-ag" style={{ '--kc': '#e59500' }}>
            <div className="kc-lab">Attività per strumento · questo mese</div>
            <div className="kc-agz">
              {STRU_C.map(s => {
                const t = cStr[s.key] || { tot: 0, serie: [] };
                const Ico = STRU_ICO[s.key];
                return (
                  <div key={s.key} className="kc-agr">
                    <span className="kc-ai" style={{ color: s.color }}><Ico size={14} /></span>
                    <span className="kc-an">{s.label}</span>
                    <span className="kc-as"><Spark data={t.serie} h={20} color={s.color} fill={false} /></span>
                    <span className="kc-av" style={{ color: s.color }}>{t.tot}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="kc kc-comp" style={{ '--kc': '#22c55e' }}>
            <div className="kc-lab">Composizione mese</div>
            <div className="kc-comp-row">
              <Donut pct={pctNon} c1="#c81e5c" c2="rgba(229,149,0,.85)" size={76} />
              <div className="kc-legend">
                <div><b style={{ color: '#c81e5c' }}>{cNon}</b> NON TIM · <b style={{ color: '#c81e5c' }}>{Math.round(pctNon * 100)}%</b></div>
                <div><b style={{ color: '#e59500' }}>{cTim}</b> TIM · <b style={{ color: '#e59500' }}>{cTot ? Math.round((cTim / cTot) * 100) : 0}%</b></div>
              </div>
            </div>
          </div>
        </div>

        {/* NUMERO */}
        {tool === 'num' && (
          <div className="console t-num" data-tour="tool">
            <div className="chead"><div className="big"><IcoNum size={22} /></div>
              <div><h2>Verifica <b>Numero</b></h2><p>{caps.vodafone ? 'Chiama il 456 e, se non è TIM, controlla in automatico se è Vodafone.' : 'Chiama il servizio 456 per sapere se la numerazione è TIM.'}</p></div></div>
            <div className="cbody">
              <div className="formcol">
                <div className="field"><label>Numero di telefono</label>
                  <input className="in" value={numero} onChange={e => setNumero(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !numRunning) avviaNumero(); }} placeholder="es. 06 8412 3390" /></div>
                <button className="btn" onClick={avviaNumero} disabled={numRunning}>{numRunning ? 'Verifica in corso…' : 'Verifica numero'}</button>
                <div className="hint">Il risultato non viene salvato nel tuo archivio.</div>
              </div>
              <div className={`stage${(!jNum) ? ' idle' : ''}`}>
                {!jNum && <span>L'esito della verifica comparirà qui.</span>}
                {jNum && numRunning && (
                  <div className="prog">
                    <div className="radar"><div className="wave"><i style={{ animationDelay: '0s' }} /><i style={{ animationDelay: '.15s' }} /><i style={{ animationDelay: '.3s' }} /><i style={{ animationDelay: '.15s' }} /><i style={{ animationDelay: '0s' }} /></div></div>
                    <div className="ph">{jNum.stato === 'in_coda' ? 'In coda…' : (NUM_PHASES[jNum.fase] || 'Verifica in corso…')}</div>
                    <div className="bar"><i style={{ width: (jNum.prog || 4) + '%' }} /></div>
                  </div>
                )}
                {jNum && jNum.done && (
                  jNum.ok ? <>
                    <div className="verdict">
                      <div className="vring"><IcoNum size={26} /></div>
                      <div>
                        <span className={`badge ${jNum.risultato === 'TIM' ? 'b-tim' : 'b-nontim'}`}>{jNum.risultato}</span>
                        <h3 style={{ marginTop: 6 }}>{jNum.risultato === 'TIM' ? 'Numerazione TIM' : 'Numerazione non TIM'}{jNum.inattiva ? ' (non attiva)' : ''}</h3>
                        <p className="sub">{numero}</p>
                      </div>
                    </div>
                    {caps.vodafone && jNum.vf && (
                      <div className={`vf-note${jNum.vf === 'VODAFONE' ? '' : ' no'}`}>
                        <VodafoneBadge size={18} /> Rete: <b>{jNum.vf === 'VODAFONE' ? 'Vodafone' : 'non Vodafone'}</b> — controllo automatico.
                      </div>
                    )}
                    {jNum.hasReg && (
                      <AudioPlayer src={adapter.registrazioneUrl(jNum.id)} onDelete={eliminaReg} />
                    )}
                  </> : <div className="verdict"><div className="vring" style={{ borderColor: 'var(--cvf)', color: 'var(--cvf)' }}><IcoNum size={26} /></div>
                    <div><span className="badge b-err">NON RIUSCITA</span><h3 style={{ marginTop: 6 }}>Verifica non riuscita</h3><p className="sub">Riprova tra qualche secondo.</p></div></div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* COPERTURA */}
        {tool === 'cov' && (caps.copertura || caps.coperturatim) && (
          <div className="console t-cov">
            <div className="chead"><div className="big"><IcoCov size={22} /></div>
              <div><h2>Verifica <b>Copertura</b></h2><p>{caps.copertura && caps.coperturatim
                ? 'Copertura fibra/ADSL su un indirizzo, da SalesMate o dal sito TIM.'
                : caps.copertura ? 'Copertura fibra/ADSL su un indirizzo, dal pool SalesMate di sistema.'
                  : 'Copertura fibra/ADSL su un indirizzo, dal sito TIM.'}</p></div></div>
            <div className="cbody">
              <div className="formcol">
                <div className="field acdesk"><label>Indirizzo</label>
                  {/* Desktop: tre campi (Comune/Via/Civico) tutti visibili e autocompilanti, NON lo
                      stepper a un campo alla volta usato sullo smartphone (lì per questione di spazio).
                      In modalità non-stepper AddressCascade non ha tasto proprio: l'Avvia è qui sotto. */}
                  <AddressCascade variant="public" fetcher={adapter.topo} inputClass="in"
                    onChange={(a) => setCov({ provincia: a.provincia, comune: a.comune, indirizzo: a.via, civico: a.civico })} />
                </div>
                {/* Due fonti = due pulsanti. Con una sola capability ne resta uno, senza
                    chiedere a chi lavora una scelta che non ha. */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {caps.copertura && (
                    <button className="btn" style={{ flex: '1 1 150px' }} onClick={() => avviaCopertura()}
                      title={eAgente && caps.ssmate_ko ? 'Password SalesMate da aggiornare: premi per aggiornarla, oppure usa «Sito TIM»'
                        : caps.copertura_ok === false ? 'Non disponibile in questo momento' : undefined}
                      disabled={caps.copertura_ok === false || covRunning || !(cov.provincia && cov.comune && cov.indirizzo.trim() && cov.civico.trim())}>
                      {covRunning ? 'Verifica in corso…' : caps.coperturatim ? 'SalesMate' : 'Verifica copertura'}</button>
                  )}
                  {caps.coperturatim && (
                    <button className="btn" style={{ flex: '1 1 150px' }} onClick={() => avviaCoperturaTim()}
                      disabled={covRunning || !(cov.comune && cov.indirizzo.trim())}>
                      {covRunning ? 'Verifica in corso…' : caps.copertura ? 'Sito TIM' : 'Verifica copertura'}</button>
                  )}
                </div>
                {/* Credenziali dell'AGENTE scadute: il lavoro non si ferma, cambia fonte. */}
                {eAgente && caps.ssmate_ko && (
                  <div className="hint" style={{ color: 'var(--warn)' }}>
                    SalesMate aspetta la tua nuova password.
                    {caps.coperturatim ? ' Intanto la copertura la trovi dal sito TIM.' : ''}
                  </div>
                )}
                {/* Pool fermo: il pulsante e' spento e si dice cosa fare, senza istruzioni
                    interne — chi usa il link non puo' sistemare i nostri account. */}
                {caps.copertura && caps.copertura_ok === false && (
                  <div className="hint" style={{ color: 'var(--warn)' }}>
                    La verifica SalesMate non è disponibile in questo momento.
                    {caps.coperturatim ? ' Usa il sito TIM qui accanto.' : ' Segnalalo al tuo referente.'}
                  </div>
                )}
                {caps.copertura && caps.coperturatim && caps.copertura_ok !== false && (
                  <div className="hint">SalesMate dà la vendibilità completa, tecnologia per tecnologia. Il sito TIM dà
                    fibra/ADSL con le velocità, e risponde anche quando SalesMate non è disponibile.</div>
                )}
                <div className="hint">Scrivi l'indirizzo col toponimo (es. VIA BRUNO GALAS) e scegli dai suggerimenti.</div>
              </div>
              <div className={`stage${(!jCov) ? ' idle' : ''}`}>
                {!jCov && <span>L'esito della copertura comparirà qui.</span>}
                {jCov && covRunning && (
                  <div className="prog">
                    <div className="radar"><IcoCov size={24} /></div>
                    <div className="ph">{jCov.stato === 'in_coda' ? `In coda…${jCov.posizione ? ` (${jCov.posizione}° in fila)` : ''}` : 'Analisi copertura in corso…'}</div>
                    <div className="bar"><i style={{ width: (jCov.prog || 4) + '%' }} /></div>
                  </div>
                )}
                {jCov && jCov.done && jCov.fonte === 'tim' && (
                  jCov.ok ? <>
                    <div className="verdict">
                      <div className="vring"><IcoCov size={26} /></div>
                      <div><span className="badge" style={{ background: esitoColorTim(jCov.risultato?.esito), color: '#fff' }}>{jCov.risultato?.esito || '—'}</span>
                        <h3 style={{ marginTop: 6 }} className="best">{jCov.risultato?.tecnologia || 'Tecnologia non indicata'}</h3>
                        <p className="sub">{jCov.risultato?.indirizzo_norm || covIndirizzo(jCov.risultato?.indirizzo)}</p></div>
                    </div>
                    <div className="covtim-righe">
                      {jCov.risultato?.raw && <div className="rowline"><span className="l">Verdetto</span><span>{jCov.risultato.raw}</span></div>}
                      <div className="rowline"><span className="l">Download</span><span>{fmtVelTim(jCov.risultato?.down) || '—'}</span></div>
                      <div className="rowline"><span className="l">Upload</span><span>{fmtVelTim(jCov.risultato?.up) || '—'}</span></div>
                      <div className="rowline"><span className="l">Latenza</span><span>{jCov.risultato?.latenza != null && jCov.risultato?.latenza !== '' ? `${jCov.risultato.latenza} ms` : '—'}</span></div>
                      {jCov.risultato?.tecnologia === 'FWA' && jCov.risultato?.down == null && jCov.risultato?.up == null && (
                        <div className="covtim-nota">Per l'FWA il sito TIM non pubblica le velocità: mostra solo la latenza.</div>
                      )}
                    </div>
                  </> : <div className="verdict"><div className="vring" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}><IcoCov size={26} /></div>
                    <div><span className="badge" style={{ background: 'rgba(245,158,11,.16)', color: 'var(--warn)' }}>NON RIUSCITA</span>
                      <h3 style={{ marginTop: 6 }}>Verifica non riuscita</h3>
                      <p className="sub">{jCov.motivo || 'Controlla l\'indirizzo o riprova.'}</p></div></div>
                )}
                {jCov && jCov.done && jCov.fonte !== 'tim' && (
                  jCov.ok ? <>
                    <div className="verdict">
                      <div className="vring"><IcoCov size={26} /></div>
                      <div><span className="badge b-cov">{miglioreTecnologia(jCov.risultato) === 'Nessuna copertura' ? 'NON COPERTO' : 'COPERTO'}</span>
                        <h3 style={{ marginTop: 6 }} className="best">{miglioreTecnologia(jCov.risultato)}</h3>
                        <p className="sub">{covIndirizzo(jCov.risultato?.indirizzo)}</p></div>
                    </div>
                    <CovMatrice d={jCov.risultato} utente={caps.nome} />
                  </> : <div className="verdict"><div className="vring" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}><IcoCov size={26} /></div>
                    <div><span className="badge" style={{ background: 'rgba(245,158,11,.16)', color: 'var(--warn)' }}>{jCov.credenziali ? 'NON DISPONIBILE' : covStato(jCov.risultato) === 'nontrovato' ? 'NON TROVATO' : 'NON RIUSCITA'}</span>
                      <h3 style={{ marginTop: 6 }}>{jCov.credenziali ? 'Verifica SalesMate non disponibile' : covStato(jCov.risultato) === 'nontrovato' ? 'Indirizzo non trovato' : 'Verifica non riuscita'}</h3>
                      {/* Il motivo vero, non «controlla l'indirizzo»: se il problema e' nostro
                          va detto, altrimenti chi lavora riprova all'infinito su un indirizzo giusto. */}
                      <p className="sub">{jCov.motivo || 'Controlla l\'indirizzo o riprova.'}</p>
                      {jCov.credenziali && caps.coperturatim && (
                        <p className="sub" style={{ marginTop: 6 }}>Puoi verificare lo stesso indirizzo dal <b>sito TIM</b>, qui sopra.</p>
                      )}</div></div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DOMINIO */}
        {tool === 'dom' && caps.dominio && (
          <div className="console t-dom">
            <div className="chead"><div className="big"><IcoDom size={22} /></div>
              <div><h2>Verifica <b>Dominio</b></h2><p>Da un sito ricava azienda e recapiti, poi lancia 456 + Copertura + Lucchetto (pool SalesMate di sistema).</p></div></div>
            <div className="cbody">
              <div className="formcol">
                <div className="field"><label>Dominio del sito</label>
                  <input className="in" value={dominioVal} onChange={e => setDominioVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !domRunning) avviaDominio(); }} placeholder="es. azienda.it" /></div>
                <button className="btn" onClick={() => avviaDominio()} disabled={domRunning}>{domRunning ? 'Analisi in corso…' : 'Analizza dominio'}</button>
                <div className="hint">Legge P.IVA, telefono e indirizzo dal footer legale del sito.</div>
              </div>
              <div className={`stage${(!jDom) ? ' idle' : ''}`}>
                {!jDom && <span>L'esito comparirà qui.</span>}
                {jDom && jDom.stato === 'estrazione' && !jDom.step && (
                  <div className="prog"><div className="radar"><IcoDom size={24} /></div><div className="ph">Estrazione dati azienda…</div></div>
                )}
                {jDom && (jDom.step || (jDom.dati && Object.keys(jDom.dati).length > 0)) && (
                  jDom.dati?.errore
                    ? <div className="verdict"><div className="vring" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}><IcoDom size={26} /></div>
                        <div><span className="badge" style={{ background: 'rgba(245,158,11,.16)', color: 'var(--warn)' }}>NON RIUSCITA</span><h3 style={{ marginTop: 6 }}>{jDom.dati.errore}</h3></div></div>
                    : <>
                        <div className="dominfo"><b>{jDom.dati?.ragione_sociale || jDom.dominio}</b><br />
                          {jDom.dati?.partita_iva ? `P.IVA ${jDom.dati.partita_iva}` : ''}{jDom.dati?.telefono ? ` · ${jDom.dati.telefono}` : ''}<br />
                          {jDom.dati?.indirizzo || ''}</div>
                        <div className="dsteps">
                          {DOM_STEPS.map(st => { const s = jDom.step?.[st.k];
                            return (<div key={st.k} className="dstep">
                              <span className="dcd" style={domStepDone(s) ? { background: st.c } : undefined} />
                              <div className="dtx"><b>{st.l}</b></div>
                              <span className="de">{domStepText(s)}</span>
                            </div>);
                          })}
                        </div>
                      </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LUCCHETTO: presidio commerciale TIM per codice fiscale (solo agente) */}
        {tool === 'luc' && caps.lucchetto && adapter.lucchettoStart && (
          <div className="console t-luc">
            <div className="chead"><div className="big"><IcoLuc size={22} /></div>
              <div><h2>Verifica <b>Lucchetto</b></h2><p>Presidio commerciale TIM per codice fiscale o partita IVA.</p></div></div>
            <div className="cbody">
              <div className="formcol">
                <div className="field"><label>Codice fiscale / P.IVA</label>
                  <input className="in" value={cf} onChange={e => setCf(e.target.value.toUpperCase())}
                    onKeyDown={e => { if (e.key === 'Enter' && !lucRunning) avviaLucchetto(); }}
                    placeholder="es. 01234567890" /></div>
                <button className="btn" onClick={avviaLucchetto} disabled={lucRunning || !cf.trim()}>
                  {lucRunning ? 'Verifica in corso…' : 'Verifica lucchetto'}</button>
                <div className="hint">Dice se l'azienda è già presidiata da un altro punto vendita TIM.</div>
              </div>
              <div className={`stage${(!jLuc) ? ' idle' : ''}`}>
                {!jLuc && <span>L'esito del lucchetto comparirà qui.</span>}
                {jLuc && lucRunning && (
                  <div className="prog"><div className="radar"><IcoLuc size={24} /></div>
                    <div className="ph">Interrogo SalesMate…</div>
                    <div className="bar"><i style={{ width: '100%' }} /></div></div>
                )}
                {jLuc && jLuc.done && (() => {
                  const r = jLuc.risultato || {};
                  const esito = r.lucchetto || r.esito;
                  const ko = !esito || jLuc.stato === 'errore';
                  return (
                    <div className="verdict">
                      <div className="vring" style={ko ? { borderColor: 'var(--warn)', color: 'var(--warn)' } : undefined}><IcoLuc size={26} /></div>
                      <div>
                        <span className="badge" style={{ background: 'rgba(240,196,106,.18)', color: 'var(--gold)' }}>{esito || 'NON RIUSCITA'}</span>
                        <h3 style={{ marginTop: 6 }}>{r.ragione_sociale || jLuc.cf}</h3>
                        <p className="sub">{r.errore || (esito ? `Codice fiscale ${jLuc.cf}` : 'Riprova tra qualche secondo.')}</p>
                        {esito && (
                          <button className="btn" style={{ marginTop: 10 }}
                            onClick={() => setLucAperto({ lucchetto: esito, codiceFiscale: jLuc.cf,
                                                          ragioneSociale: r.ragione_sociale, lavorabile: r.lavorabile,
                                                          dettaglio: r.dettaglio })}>
                            Vedi le consistenze
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* INDAGA: da un numero risali all'azienda (reverse lookup Google Maps) */}
        {tool === 'ind' && caps.indaga && (
          <div className="console t-ind">
            <div className="chead"><div className="big"><IcoInd size={22} /></div>
              <div><h2>Indaga <b>Numero</b></h2></div></div>
            <div className="cbody">
              <div className="formcol">
                <div className="field"><label>Numero di telefono</label>
                  <input className="in" value={indagaNum} onChange={e => setIndagaNum(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !indRunning) avviaIndaga(); }} placeholder="es. 06 8412 3390" /></div>
                <button className="btn" onClick={() => avviaIndaga()} disabled={indRunning}>{indRunning ? 'Ricerca in corso…' : 'Indaga numero'}</button>
              </div>
              <div className={`stage${(!jInd) ? ' idle' : ''}`}>
                {!jInd && <span>L'esito della ricerca comparirà qui.</span>}
                {jInd && indRunning && (
                  <div className="prog"><div className="radar"><IcoInd size={24} /></div><div className="ph">Ricerca in corso…</div></div>
                )}
                {jInd && jInd.done && (
                  jInd.dati?.ragione_sociale ? <>
                    <div className="verdict">
                      <div className="vring"><IcoInd size={26} /></div>
                      <div><span className="badge" style={{ background: 'rgba(229,149,0,.2)', color: 'var(--c456)' }}>TROVATA</span>
                        <h3 style={{ marginTop: 6 }}>{jInd.dati.ragione_sociale}</h3>
                        <p className="sub">{indagaNum}</p></div>
                    </div>
                    <div style={{ fontSize: 13.5, display: 'grid', gap: 5, color: 'var(--muted)', marginTop: 10 }}>
                      {jInd.dati.categoria && <div><b style={{ color: 'var(--ink)' }}>Categoria:</b> {jInd.dati.categoria}</div>}
                      {jInd.dati.indirizzo && <div><b style={{ color: 'var(--ink)' }}>Indirizzo:</b> {jInd.dati.indirizzo}</div>}
                      {(jInd.dati.comune || jInd.dati.provincia) && <div><b style={{ color: 'var(--ink)' }}>Città:</b> {[jInd.dati.comune, jInd.dati.provincia && `(${jInd.dati.provincia})`].filter(Boolean).join(' ')}</div>}
                      {jInd.dati.telefono_maps && <div><b style={{ color: 'var(--ink)' }}>Telefono:</b> {jInd.dati.telefono_maps}</div>}
                      {jInd.dati.partita_iva && <div><b style={{ color: 'var(--ink)' }}>P.IVA:</b> {jInd.dati.partita_iva}</div>}
                      {jInd.dati.sito && <div><b style={{ color: 'var(--ink)' }}>Sito:</b> <a href={jInd.dati.sito} target="_blank" rel="noreferrer" style={{ color: 'var(--c456)' }}>{jInd.dati.sito.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></div>}
                      {jInd.dati.email && <div><b style={{ color: 'var(--ink)' }}>Email:</b> {jInd.dati.email}</div>}
                      {jInd.dati.maps_url && <div style={{ marginTop: 4 }}><a href={jInd.dati.maps_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c456)' }}>Apri mappa ↗</a></div>}
                    </div>
                  </> : <div className="verdict"><div className="vring" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}><IcoInd size={26} /></div>
                    <div><span className="badge" style={{ background: 'rgba(245,158,11,.16)', color: 'var(--warn)' }}>{jInd.dati?.errore ? 'NON RIUSCITA' : 'NESSUN RISULTATO'}</span>
                      <h3 style={{ marginTop: 6 }}>{jInd.dati?.errore ? 'Ricerca non riuscita' : 'Nessuna azienda trovata'}</h3>
                      <p className="sub">{jInd.dati?.errore ? 'Riprova tra qualche secondo.' : 'Nessun risultato per questo numero.'}</p></div></div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VICINE (desktop): aziende attorno a un punto scelto sulla mappa */}
        {tool === 'zona' && caps.vicine && (
          <div className="console t-vic">
            <div className="chead"><div className="big"><IcoZona size={22} /></div>
              <div><h2>Aziende per <b>zona</b></h2><p>Clicca un punto sulla mappa e trova le aziende in quella zona (Google Places), da cui lanciare le verifiche.</p></div></div>
            <div className="cbody single" style={{ gridTemplateColumns: '1fr' }}>
              <div className="formcol">
                <div className="vic-ctrl" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                  <div className="rseg">
                    {[{ v: 500, l: '500 m' }, { v: 1000, l: '1 km' }, { v: 2000, l: '2 km' }, { v: 5000, l: '5 km' }].map(r => (
                      <button key={r.v} className={vicMap.raggio === r.v ? 'on' : ''} onClick={() => vicMap.setRaggio(r.v)}>{r.l}</button>
                    ))}
                  </div>
                  <button className="btn" style={{ width: 'auto', marginTop: 0 }} disabled={!punto || vicMap.busy}
                    onClick={() => punto && vicMap.cercaCoord(punto.lat, punto.lon)}>{vicMap.busy ? 'Cerco…' : 'Trova aziende in questa zona'}</button>
                </div>
                {/* La mappa prende tutta l'altezza che resta, fino in fondo: la pagina non
                    scorre (la console e' a altezza fissa) e si vede piu' territorio. */}
                <div className="vic-mappa">
                  <ZonaMap punto={punto} onPick={(lat, lon) => setPunto({ lat, lon })} height="100%"
                    aziende={vicMap.itemsFiltrati} evidenziata={azSotto || azMappa} segui={azSotto}
                    selezionate={sel} onEvidenzia={setAzMappa} />
                </div>
                <div className="vmsg">{punto ? `Punto scelto: ${punto.lat.toFixed(5)}, ${punto.lon.toFixed(5)} — le aziende trovate compaiono a destra.` : 'Clicca sulla mappa per scegliere il punto di riferimento.'}</div>
                {vicMap.msg && <div className="vmsg">{vicMap.msg}</div>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT: in Vicine mostra le AZIENDE TROVATE, altrimenti la cronologia */}
      <div className="side" data-tour="cron">
        {tool === 'zona' && caps.vicine ? (
          <>
            <div className="sh"><b>Aziende trovate</b><span>{(vicMap.itemsFiltrati || []).length || ''}</span></div>
            {(vicMap.itemsFiltrati || []).length > 0 && (
              <div className="vsel">
                <label className="vall"><input type="checkbox"
                  checked={vicMap.itemsFiltrati.length > 0 && vicMap.itemsFiltrati.every(it => sel.has(it.id))}
                  onChange={() => toggleSelTutte(vicMap.itemsFiltrati)} /> Tutte</label>
                <button className="btn vimp" disabled={sel.size === 0 || imp?.attivo} onClick={importaSel}>
                  {imp?.attivo ? `Importo… ${imp.fatti}/${imp.totale}` : `Importa selezionate (${sel.size})`}</button>
              </div>
            )}
            {imp && !imp.attivo && imp.fine && (
              <div className="vmsg" style={{ padding: '4px 14px' }}>Ultimo import: {imp.creati} in archivio · {imp.registro} da completare · {imp.gia} già presenti</div>
            )}
            <div className="hist">
              {vicMap.busy ? <div className="empty">Cerco aziende…</div>
                : (vicMap.itemsFiltrati && vicMap.itemsFiltrati.length > 0) ? (
                  <>
                    {vicMap.itemsFiltrati.map((it, i) => {
                      const a = it.archivio || {};
                      const tag = a.lead ? { c: 'lead', t: 'LEAD' } : a.risultato === 'TIM' ? { c: 'tim', t: 'TIM' } : a.trovato ? { c: 'arc', t: 'archivio' } : null;
                      const dist = it.distanza != null ? (it.distanza < 1000 ? `${it.distanza} m` : `${(it.distanza / 1000).toFixed(1)} km`) : '';
                      const gia = esitoDominio(it.sito);
                      return (
                        <div key={it.id || i} data-az={it.id}
                          className={`vrow${(azSotto || azMappa) && (azSotto || azMappa).id === it.id ? ' on' : ''}`}
                          onMouseEnter={() => setAzSotto(it)} onMouseLeave={() => setAzSotto(null)}>
                          <input type="checkbox" className="vck" checked={sel.has(it.id)} onChange={() => toggleSel(it.id)} />
                          <div className="vtx" onClick={() => vicMap.apri(it)}>
                            <div className="vn">{vicMap.analizzate && vicMap.analizzate[it.id] && <span className="vok" title="Già analizzata">✓</span>}{it.nome}</div>
                            <div className="vm">
                              {it.indirizzo}{dist ? ` · ${dist}` : ''}
                              {it.sito && <> · <a className="vsito" href={it.sito} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{dominioBreve(it.sito)}</a></>}
                            </div>
                          </div>
                          {gia && <button className="vocchio" title="Vedi l'esito della verifica dominio"
                            onClick={(e) => { e.stopPropagation(); setDetail(gia); }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
                          </button>}
                          {tag && <span className={`vt ${tag.c}`}>{tag.t}</span>}
                          {/* Il sito arriva GIA' con l'elenco (nessun credito): il dominio si
                              vede e si verifica senza aprire il dettaglio. Il 456 no, perche'
                              il telefono lo da' solo il dettaglio, che costa un credito. */}
                          {jDom && !jDom.done && jDom.da === it.id && (
                            <div className="vwork"><i /><span>Verifica in corso… puoi continuare a navigare</span></div>
                          )}
                          <div className="vact">
                            {caps.dominio && it.sito
                              ? <button className="btn t-dom" disabled={domRunning}
                                  onClick={(e) => { e.stopPropagation(); avviaDominio(it.sito, it.id); }}>
                                  {jDom && !jDom.done && jDom.da === it.id ? 'In corso…' : gia ? 'Rifai la verifica' : 'Verifica dominio'}
                                </button>
                              : <span className="vnosito">Nessun sito per questa azienda</span>}
                            <button className="vmore" style={{ margin: 0, width: 'auto', padding: '6px 12px' }}
                              onClick={(e) => { e.stopPropagation(); vicMap.apri(it); }}>Dettaglio e 456</button>
                          </div>
                        </div>
                      );
                    })}
                    {vicMap.senzaSito > 0 && <div className="vnota">{vicMap.senzaSito === 1 ? '1 società della zona senza sito web non è in elenco' : `${vicMap.senzaSito} società della zona senza sito web non sono in elenco`}</div>}
                    {vicMap.next && <button className="vmore" onClick={vicMap.caricaAltre} disabled={vicMap.more}>{vicMap.more ? 'Carico…' : 'Carica altre'}</button>}
                  </>
                ) : <div className="empty">Scegli un punto sulla mappa e premi<br />"Trova aziende in questa zona".</div>}
            </div>
          </>
        ) : (
          <>
        <div className="sh"><b>Cronologia</b><span>ultime verifiche</span></div>
        {anyRunning && (
          <div className="job"><div className="jt"><span className="jr" /> {numRunning ? 'Verifica numero' : covRunning ? 'Copertura' : domRunning ? 'Dominio' : 'Indaga'} in corso…</div>
            <div className="jbar"><i style={{ width: ((numRunning ? jNum?.prog : covRunning ? jCov?.prog : 60) || 4) + '%' }} /></div></div>
        )}
        <div className="hist">
          {crono.length === 0 ? <div className="empty">Nessuna verifica ancora.<br />Avviane una: comparirà qui.</div>
            : crono.map((d, i) => {
              // La copertura ha due fonti: stessa riga, stessa icona, fonte scritta nel sottotitolo.
              const isCovTim = d.tipo === 'coperturatim';
              const isCov = d.tipo === 'copertura' || isCovTim, isDom = d.tipo === 'dominio', isInd = d.tipo === 'indaga';
              const label = isDom ? (d.dominio || '—')
                : isCov ? `${(d.indirizzo?.comune || '').toLowerCase()} · ${(d.indirizzo?.via || '').toLowerCase()} ${d.indirizzo?.civico || ''}`
                : isInd ? ((d.dati && d.dati.ragione_sociale) || d.numero || '—')
                : d.numero;
              const apribile = isDom || (isCovTim ? !!(d.risultato && !d.risultato.errore) : (isCov && covStato(d) === 'ok'))
                || (isInd && !!(d.dati && d.dati.ragione_sociale));
              const sub = (isDom ? 'Dominio' : isCovTim ? 'Copertura · sito TIM' : isCov ? 'Copertura' : isInd ? `Indaga${d.numero ? ' · ' + d.numero : ''}` : '456') + (d.at ? ` · ${fmtData(d.at)}` : '');
              return (
                <div key={d.id || i} className="hi" style={apribile ? { cursor: 'pointer' } : undefined}
                  onClick={apribile ? () => setDetail(d) : undefined}>
                  <div className={`hic ${isCov ? 'cov' : isDom ? 'dom' : isInd ? 'ind' : 'num'}`}>{isCov ? <IcoCov size={15} /> : isDom ? <IcoDom size={15} /> : isInd ? <IcoInd size={15} /> : <IcoNum size={15} />}</div>
                  <div className="htx"><b><span>{label || '—'}</span></b><span className="hsub">{sub}</span></div>
                  {isInd
                    ? <div className="he-eye">{apribile
                        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></svg>
                        : <span className="he-none">—</span>}</div>
                    : <div className="he" style={esitoStyle(d)}>{esitoText(d)}</div>}
                </div>
              );
            })}
        </div>
          </>
        )}
      </div>
    </div>

    {detail && (
      <div className="pscrim" onClick={() => setDetail(null)}>
        <div className={`pmodal ${detail.tipo === 'dominio' ? 't-dom' : detail.tipo === 'indaga' ? 't-ind' : 't-cov'}`} onClick={e => e.stopPropagation()}>
          {detail.tipo === 'indaga' ? <>
            <h3>{detail.dati?.ragione_sociale || detail.numero}</h3>
            <div className="pl">Numero: {detail.numero}</div>
            {detail.dati?.categoria && <div className="pl">{detail.dati.categoria}</div>}
            {detail.dati?.indirizzo && <div className="pl">{detail.dati.indirizzo}</div>}
            {(detail.dati?.comune || detail.dati?.provincia) && <div className="pl">{[detail.dati.comune, detail.dati.provincia && `(${detail.dati.provincia})`].filter(Boolean).join(' ')}</div>}
            {detail.dati?.telefono_maps && <div className="pl">Tel: {detail.dati.telefono_maps}</div>}
            {detail.dati?.partita_iva && <div className="pl">P.IVA {detail.dati.partita_iva}</div>}
            {detail.dati?.sito && <div className="pl"><a href={detail.dati.sito} target="_blank" rel="noreferrer" style={{ color: 'var(--c456)' }}>{detail.dati.sito.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a></div>}
            {detail.dati?.email && <div className="pl">{detail.dati.email}</div>}
            {detail.dati?.maps_url && <div className="pl"><a href={detail.dati.maps_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c456)' }}>Apri mappa ↗</a></div>}
          </> : detail.tipo === 'dominio' ? <>
            <h3>{detail.dati?.ragione_sociale || detail.dominio}</h3>
            <div className="pl">{detail.dati?.partita_iva ? `P.IVA ${detail.dati.partita_iva}` : ''}{detail.dati?.telefono ? ` · ${detail.dati.telefono}` : ''}</div>
            {detail.dati?.indirizzo && <div className="pl">{detail.dati.indirizzo}</div>}
            <div className="dsteps">
              {DOM_STEPS.map(st => { const s = detail.step?.[st.k];
                return (<div key={st.k} className="dstep">
                  <span className="dcd" style={domStepDone(s) ? { background: st.c } : undefined} />
                  <div className="dtx"><b>{st.l}</b></div><span className="de">{domStepText(s)}</span>
                </div>); })}
            </div>
          </> : detail.tipo === 'coperturatim' ? <>
            {/* Il sito TIM risponde con un verdetto e le velocita', non con la matrice */}
            <h3>Copertura · sito TIM</h3>
            <div className="pl">{`${detail.indirizzo?.comune || ''} · ${detail.indirizzo?.via || ''} ${detail.indirizzo?.civico || ''}`}</div>
            <div className="best" style={{ margin: '8px 0 4px' }}>{detail.risultato?.tecnologia || detail.esito || '—'}</div>
            <div className="covtim-righe">
              {detail.risultato?.raw && <div className="rowline"><span className="l">Verdetto</span><span>{detail.risultato.raw}</span></div>}
              <div className="rowline"><span className="l">Download</span><span>{fmtVelTim(detail.risultato?.down) || '—'}</span></div>
              <div className="rowline"><span className="l">Upload</span><span>{fmtVelTim(detail.risultato?.up) || '—'}</span></div>
              <div className="rowline"><span className="l">Latenza</span><span>{detail.risultato?.latenza != null && detail.risultato?.latenza !== '' ? `${detail.risultato.latenza} ms` : '—'}</span></div>
              {detail.risultato?.indirizzo_norm && <div className="rowline"><span className="l">Indirizzo</span><span>{detail.risultato.indirizzo_norm}</span></div>}
            </div>
          </> : <>
            <h3>Copertura</h3>
            <div className="pl">{`${detail.indirizzo?.comune || ''} · ${detail.indirizzo?.via || ''} ${detail.indirizzo?.civico || ''}`}</div>
            <div className="best" style={{ margin: '8px 0 4px' }}>{miglioreTecnologia(detail)}</div>
            <CovMatrice d={detail} utente={caps.nome} />
          </>}
          <button className="vmore" style={{ marginTop: 12 }} onClick={() => setDetail(null)}>Chiudi</button>
        </div>
      </div>
    )}

    {notifOpen && adapter.loadNotifiche && (
      <div className="pscrim" onClick={() => setNotifOpen(false)}>
        <div className="pmodal t-num" onClick={e => e.stopPropagation()} style={{ width: 'min(460px,94vw)' }}>
          <h3>Notifiche</h3>
          {notifPerm !== 'unsupported' && notifPerm !== 'granted' && (
            <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '9px 11px', marginBottom: 10 }}>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                {notifPerm === 'denied'
                  ? 'Le notifiche di Windows sono bloccate dal browser: sbloccale dalle sue impostazioni per riceverle anche quando la finestra non è in primo piano.'
                  : 'Attiva le notifiche di Windows per essere avvisato anche quando la finestra non è in primo piano.'}
              </div>
              {notifPerm !== 'denied' && (
                <button className="vmore" style={{ marginTop: 8 }} onClick={attivaNotifiche}>Attiva sul desktop</button>
              )}
            </div>
          )}
          {notifiche.length === 0 ? (
            <p className="pl">Nessuna notifica da leggere.</p>
          ) : (
            <>
              <div style={{ maxHeight: '52vh', overflow: 'auto', display: 'grid', gap: 8, margin: '10px 0' }}>
                {notifiche.map(n => (
                  <div key={n.id} style={{ border: '1px solid var(--line)', borderRadius: 10, padding: '9px 11px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{n.title}</div>
                    {n.body && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{n.body}</div>}
                    <button className="vmore" style={{ marginTop: 8 }}
                      onClick={() => { adapter.notificaLetta(n.id).catch(() => {}); setNotifiche(v => v.filter(x => x.id !== n.id)); }}>
                      Segna come letta
                    </button>
                  </div>
                ))}
              </div>
              <button className="btn" onClick={() => { adapter.notificheLetteTutte().catch(() => {}); setNotifiche([]); }}>
                Segna tutte come lette
              </button>
            </>
          )}
          <button className="vmore" style={{ marginTop: 10 }} onClick={() => setNotifOpen(false)}>Chiudi</button>
        </div>
      </div>
    )}

    {/* Consistenze del Lucchetto: stesso riquadro della PWA, in veste scura */}
    {lucAperto && (
      <div className="pscrim" onClick={() => setLucAperto(null)}>
        <div onClick={e => e.stopPropagation()}>
          <LucchettoConsistenze
            lucchetto={lucAperto.lucchetto}
            codiceFiscale={lucAperto.codiceFiscale}
            ragioneSociale={lucAperto.ragioneSociale}
            lavorabile={lucAperto.lavorabile}
            dettaglio={lucAperto.dettaglio}
            screenshotUrl={adapter.lucchettoScreenshotUrl ? adapter.lucchettoScreenshotUrl(lucAperto.codiceFiscale) : null}
            onClose={() => setLucAperto(null)}
            dark
          />
        </div>
      </div>
    )}

    {/* Password SalesMate: la porta per rientrare quando TIM la fa cambiare */}
    {ssmOpen && (
      <div className="pscrim" onClick={() => !ssmBusy && setSsmOpen(false)}>
        <div className="pmodal t-num" onClick={e => e.stopPropagation()} style={{ width: 'min(400px,94vw)' }}>
          <h3>Credenziali SalesMate</h3>
          <p className="pl" style={{ color: caps.ssmate_ko ? 'var(--cvf)' : 'var(--muted)' }}>
            {caps.ssmate_ko
              ? 'Le credenziali SalesMate non sono più valide: Copertura e Lucchetto sono bloccati. Reinserisci la password per sbloccarli.'
              : 'È l’unica password: vale per l’accesso all’app e per Copertura e Lucchetto.'}
          </p>
          <input className="in" disabled value={caps.username || caps.nome || ''} style={{ marginBottom: 10, opacity: .7 }} />
          <input className="in" type="password" placeholder="Password SalesMate" value={ssmPwd}
            onChange={e => setSsmPwd(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') salvaSsmate(); }} style={{ marginBottom: 12 }} />
          {ssmErr && <div style={{ color: 'var(--cvf)', fontSize: 13, marginBottom: 10 }}>{ssmErr}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="vmore" style={{ flex: 1 }} disabled={ssmBusy} onClick={() => setSsmOpen(false)}>Annulla</button>
            <button className="btn" style={{ flex: 1 }} disabled={ssmBusy} onClick={salvaSsmate}>{ssmBusy ? 'Verifico…' : 'Valida e salva'}</button>
          </div>
        </div>
      </div>
    )}

    {showInstall && (
      <div className="pscrim" onClick={() => setShowInstall(false)}>
        <div className="pmodal instmod t-num" onClick={e => e.stopPropagation()}>
          <div className="inst-head">
            <img src="/icon-sonitech.svg" alt="" />
            <div><h3>Installa l'app sul computer</h3>
              <div className="inst-sub">Apri le verifiche con un clic, in una finestra dedicata come un programma.
                {browser !== 'other' && <> · Rilevato <b>{{ edge: 'Microsoft Edge', chrome: 'Google Chrome', opera: 'Opera', firefox: 'Firefox', safari: 'Safari' }[browser]}</b>.</>}</div></div>
          </div>
          {installEvt && <div className="inst-fast">Il modo più veloce: premi <b>“Installa app”</b> qui sotto.</div>}
          {(() => {
            // Guida per browser, verificata: si punta all'ICONA nella barra indirizzi (posizione + tooltip,
            // riferimenti sempre validi) e ai percorsi di menu attuali. Firefox desktop non supporta l'install.
            const GUIDE = {
              edge: [
                <>Nella <b>barra degli indirizzi</b>, a <b>destra</b> — subito prima della <b>stella</b> dei Preferiti — clicca l'icona <InstallGlyph /> <b>Installa app</b> (passandoci sopra compare «Installa TIM 456…»).</>,
                <>In alternativa: menu <b>“…”</b> (Impostazioni e altro, in alto a destra) → <b>App</b> → <b>“Installa questo sito come app”</b>.</>,
                <>Conferma <b>“Installa”</b>: si apre in una sua finestra.</>,
              ],
              chrome: [
                <>Nella <b>barra degli indirizzi</b>, a <b>destra</b>, clicca l'icona <InstallGlyph /> <b>Installa</b> (passandoci sopra dice «Installa …»).</>,
                <>In alternativa: menu <b>“⋮”</b> (in alto a destra) → <b>“Installa …”</b> (in alcune versioni sotto <b>“Salva e condividi”</b>).</>,
                <>Conferma <b>“Installa”</b>.</>,
              ],
              opera: [
                <>Nella <b>barra degli indirizzi</b>, a destra, clicca l'icona <InstallGlyph /> <b>Installa</b>; in alternativa menu <b>Opera</b> → <b>“Installa …”</b>.</>,
                <>Conferma <b>“Installa”</b>.</>,
              ],
              safari: [
                <>Menu <b>File</b> → <b>“Aggiungi al Dock”</b> (Safari 17 / macOS Sonoma o successivi), oppure icona <b>Condividi</b> → “Aggiungi al Dock”.</>,
                <>L'app comparirà nel <b>Dock</b> e si aprirà in una sua finestra.</>,
              ],
              firefox: [
                <><b>Firefox</b> sul computer non installa le app web. Apri questo link con <b>Microsoft Edge</b> o <b>Google Chrome</b> per installarla.</>,
              ],
              other: [
                <>Cerca l'icona <InstallGlyph /> <b>Installa</b> nella barra degli indirizzi, oppure la voce <b>“Installa app”</b> nel menu del browser.</>,
                <>Conferma per aggiungerla come programma.</>,
              ],
            };
            return <ol className="inst-steps">{(GUIDE[browser] || GUIDE.other).map((s, i) => <li key={i}>{s}</li>)}</ol>;
          })()}
          <div className="inst-acts">
            {installEvt && <button className="btn" style={{ width: 'auto' }} onClick={installApp}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></svg>
              Installa app
            </button>}
            <button className="inst-skip" onClick={() => setShowInstall(false)}>Salta</button>
          </div>
        </div>
      </div>
    )}

    {toasts.length > 0 && (
      <div className="toasts">{toasts.map(t => <div key={t.id} className="toast"><div><h5>{t.titolo}</h5><p>{t.sub}</p></div></div>)}</div>
    )}
  </>);
}
